import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router";
import { Show, RedirectToSignIn } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { SentryErrorBoundary } from "@/lib/sentry";
import { LayoutShell } from "@/components/layout-shell";

// Search is the landing route — keep eager so the search input is interactive
// on first paint. Cohort is the heavy route (roster grid + attendance + QR +
// dialogs) — lazy-load it so first-byte for /-search users stays small.
import { Search } from "@/routes/search";
const Cohort = lazy(() =>
  import("@/routes/cohort").then((m) => ({ default: m.Cohort })),
);

function CohortRouteFallback() {
  return (
    <div className="flex flex-col gap-6 p-6">
      <Skeleton className="h-7 w-80" />
      <Skeleton className="h-4 w-64" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-8">
      <p className="text-muted-foreground">Page not found</p>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: (
      <>
        <Show when="signed-out">
          <RedirectToSignIn
            redirectUrl={window.location.pathname + window.location.search}
          />
        </Show>
        <Show when="signed-in">
          <LayoutShell />
        </Show>
      </>
    ),
    children: [
      { path: "/", element: <Search /> },
      {
        path: "/cohort/:cohortNumber",
        element: (
          <Suspense fallback={<CohortRouteFallback />}>
            <Cohort />
          </Suspense>
        ),
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

export default function App() {
  return (
    <SentryErrorBoundary
      fallback={
        <div className="flex min-h-screen items-center justify-center p-8">
          <p className="text-destructive">
            An unexpected error occurred. Please reload the page.
          </p>
        </div>
      }
    >
      <RouterProvider router={router} />
      <Toaster />
    </SentryErrorBoundary>
  );
}
